/**
 * The ⚙ button and its panel: General (HUD layout, graphics quality, the
 * host's End match), Hotkeys (view/hotkeys.ts) and Help (how the match is won, what the
 * score counts, roster tips). Opens below the button; Esc (useHotkeys), the
 * button or a click outside closes it. Open state is `settingsOpen` in the UI
 * store so Esc closes it before anything else.
 */
import { useEffect, useRef } from 'react';
import { matchDuration, matchHost } from '@/game/sim';
import { HOTKEYS } from '@/game/view/hotkeys';
import { clockLabel } from '@/game/view/matchClock';
import { liveScore } from '@/game/view/score';
import { useSimFrame } from '../app/simFrame';
import { usePersisted } from '../persisted';
import { QualityControl, type useQuality } from '../QualitySettings';
import { type UiStore, useUi } from '../ui/store';
import { EndMatchControl } from './EndMatchControl';
import { HUD_MODES, type HudMode } from './hudMode';
import { HotkeyRow } from './KeyLegend';

type Tab = 'general' | 'hotkeys' | 'help';
const TABS: Array<[Tab, string]> = [
  ['general', 'General'],
  ['hotkeys', 'Hotkeys'],
  ['help', 'Help'],
];

export function SettingsPanel({
  ui,
  quality,
  mode,
  onMode,
  onEndMatch,
}: {
  ui: UiStore;
  quality: ReturnType<typeof useQuality>;
  mode: HudMode;
  onMode: (mode: HudMode) => void;
  onEndMatch: () => void;
}) {
  const open = useUi(ui, (s) => s.settingsOpen);
  const [savedTab, setTab] = usePersisted<string>('settingsTab', 'general');
  const tab = TABS.find(([t]) => t === savedTab)?.[0] ?? 'general';
  const root = useRef<HTMLDivElement>(null);

  // A press anywhere outside closes it (the map stays usable: the press goes through).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) ui.set({ settingsOpen: false });
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, ui]);

  return (
    <div ref={root} className="pointer-events-auto relative">
      <button
        type="button"
        aria-expanded={open}
        aria-label="Settings"
        title="Settings: quality, hotkeys, help"
        onClick={() => ui.set((s) => ({ settingsOpen: !s.settingsOpen }))}
        className={`btn btn-ghost btn-icon h-8 w-8 text-base ${open ? 'bg-fill-hover' : ''}`}
      >
        ⚙
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Settings"
          className="panel absolute right-0 top-full z-30 mt-2 flex max-h-[calc(100vh-5rem)] w-80 flex-col gap-2 p-3 text-xs"
        >
          <div className="flex items-center gap-2">
            <p className="panel-title">Settings</p>
            <div
              role="tablist"
              className="ml-auto flex gap-0.5 overflow-hidden rounded-full bg-fill p-0.5"
            >
              {TABS.map(([t, label]) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={t === tab}
                  onClick={() => setTab(t)}
                  className={`chip px-1.5 py-0.5 transition ${t === tab ? 'chip-selected font-medium' : 'bg-transparent text-fg-muted hover:bg-fill-hover'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="min-h-0 overflow-y-auto">
            {tab === 'general' && (
              <GeneralTab quality={quality} mode={mode} onMode={onMode} onEndMatch={onEndMatch} />
            )}
            {tab === 'hotkeys' && <HotkeysTab />}
            {tab === 'help' && <HelpTab />}
          </div>
        </div>
      )}
    </div>
  );
}

function GeneralTab({
  quality,
  mode,
  onMode,
  onEndMatch,
}: {
  quality: ReturnType<typeof useQuality>;
  mode: HudMode;
  onMode: (mode: HudMode) => void;
  onEndMatch: () => void;
}) {
  const { real, playerId } = useSimFrame();
  // Only the match host may end it early (the sim checks the same, `matchHost`).
  const isMatchHost = matchHost(real) === playerId;
  return (
    <div className="flex flex-col gap-2">
      {/* biome-ignore lint/a11y/useSemanticElements: a pill of toggle buttons, as QualityControl */}
      <div
        role="group"
        aria-label="HUD layout"
        className="pill flex h-8 items-center gap-1 px-1.5 text-xs"
      >
        <span className="px-1 text-fg-subtle">Layout</span>
        {HUD_MODES.map((m) => (
          <button
            key={m.mode}
            type="button"
            aria-pressed={mode === m.mode}
            title={m.title}
            onClick={() => onMode(m.mode)}
            className={`chip cursor-pointer ${mode === m.mode ? 'chip-selected' : 'hover:bg-fill-hover'}`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <QualityControl
        setting={quality.setting}
        autoLow={quality.autoLow}
        onChange={quality.setSetting}
      />
      {isMatchHost && <EndMatchControl onEnd={onEndMatch} />}
    </div>
  );
}

function HotkeysTab() {
  return (
    <div className="flex flex-col gap-2">
      {HOTKEYS.map((group) => (
        <section key={group.title}>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
            {group.title}
          </p>
          {group.keys.map((k) => (
            <HotkeyRow key={k.action} hotkey={k} />
          ))}
        </section>
      ))}
    </div>
  );
}

function HelpTab() {
  const { view: state, real, playerId, rules } = useSimFrame();
  const duration = matchDuration(state, rules);
  const score = liveScore(real, rules, playerId);
  const r = Math.round;
  return (
    <div className="flex flex-col gap-2 text-fg-muted">
      <section>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
          Winning
        </p>
        <p>
          The last player standing wins. A player is out once they have no cities (sites count) and
          no settlers.{' '}
          {duration === null
            ? 'There is no time limit: the host can end the match, and then the highest score wins.'
            : `At the ${clockLabel(Math.round(duration))} time limit, or when the host ends the match, the highest score wins.`}
        </p>
      </section>
      <section>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
          Score
        </p>
        <p>
          Stock in your cities counts ×{score.stockWeight}, so spend it. Buildings count at their
          cost, city levels ×{score.cityLevelWeight}
          {score.armyCounts ? ', and your army at its training cost' : ''}.
        </p>
        {!state.players[playerId]?.eliminated && (
          <p className="mt-1 tabular-nums text-fg">
            You: {r(score.total).toLocaleString('en-GB')}
            <span className="text-fg-subtle">
              {' '}
              · stock {r(score.stock)} · buildings {r(score.buildings)}
              {score.armyCounts ? ` · army ${r(score.army)}` : ''}
            </span>
          </p>
        )}
      </section>
      <section>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
          Units
        </p>
        <p>
          Click a type or group in the roster to select all of it; shift-click adds. ⬆ upgrades and
          ★ ranks up as many as you can afford.
        </p>
      </section>
    </div>
  );
}
